/**
 * Typed error classes for doc-cirrus-bridge
 *
 * Uses Data.TaggedError pattern for typed, serializable errors compatible with RPC
 */

import { Data, Schema } from "effect";

/**
 * Validation error for schema validation failures
 */
export class ValidationError extends Data.TaggedError("ValidationError")<{
  message: string;
  errors: unknown;
}> {}

/**
 * Resource not found error
 */
export class NotFoundError extends Data.TaggedError("NotFoundError")<{
  message: string;
  resource: string;
  id?: string;
}> {}

/**
 * External API error for DocCirrus API failures
 */
export class ExternalAPIError extends Data.TaggedError("ExternalAPIError")<{
  message: string;
  statusCode?: number;
  /** Safe internal producer category; no raw decoder/transport cause is required. */
  failureKind?: "transport" | "decode";
  cause?: unknown;
}> {}

/**
 * Internal server error
 */
export class InternalError extends Data.TaggedError("InternalError")<{
  message: string;
  cause?: unknown;
}> {}

/**
 * Terminal not found error for card reader operations
 */
export class TerminalNotFoundError extends Data.TaggedError(
  "TerminalNotFoundError",
)<{
  message: string;
  terminalId: string;
}> {}

/**
 * An eGK is inserted in the terminal, but inSuite returned no patient data for it.
 *
 * Card reading goes through inSuite only; this error is the truthful outcome of an
 * empty inSuite payload and is never replaced by a direct Konnektor read.
 *
 * @since 1.1.7
 * @example
 * ```ts
 * const error = new CardDataUnavailableError({ terminalId: "T1", reason: "empty-payload" });
 * ```
 */
export class CardDataUnavailableError extends Data.TaggedError(
  "CardDataUnavailableError",
)<{
  terminalId: string;
  reason: "empty-payload";
}> {}

/**
 * Consult times not found error for calendar operations
 */
export class ConsultTimesNotFoundError extends Data.TaggedError(
  "ConsultTimesNotFoundError",
)<{
  message: string;
  calendarId: string;
}> {}

// Export error schemas for RPC
export const ValidationErrorSchema = Schema.TaggedStruct("ValidationError", {
  message: Schema.String,
  errors: Schema.Unknown,
});

export { NotFoundErrorSchema } from "@ascleo/frontdesk-contracts/scheduling/errors";

export { ExternalAPIErrorSchema } from "@ascleo/frontdesk-contracts/scheduling/errors";

export const InternalErrorSchema = Schema.TaggedStruct("InternalError", {
  message: Schema.String,
  cause: Schema.optional(Schema.Unknown),
});

export const TerminalNotFoundErrorSchema = Schema.TaggedStruct(
  "TerminalNotFoundError",
  {
    message: Schema.String,
    terminalId: Schema.String,
  },
);

/**
 * RPC mirror of {@link CardDataUnavailableError}.
 *
 * @since 1.1.7
 * @example
 * ```ts
 * const decoded = Schema.decodeUnknownSync(CardDataUnavailableErrorSchema)({
 *   _tag: "CardDataUnavailableError",
 *   terminalId: "T1",
 *   reason: "empty-payload",
 * });
 * ```
 */
export const CardDataUnavailableErrorSchema = Schema.TaggedStruct(
  "CardDataUnavailableError",
  {
    terminalId: Schema.String,
    reason: Schema.Literal("empty-payload"),
  },
);

export { ConsultTimesNotFoundErrorSchema } from "@ascleo/frontdesk-contracts/scheduling/errors";
